import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';

interface Signature {
  id: string;
  name: string;
  html: string;
  assignments?: string[];
}

interface Group {
  id: string;
  displayName: string;
  mail: string;
}

interface SignatureDetailProps {
  signature: Signature;
  onBack: () => void;
  onEdit: () => void;
  onAssign: () => void;
  onDelete: (id: string) => void;
}

const SignatureDetail: React.FC<SignatureDetailProps> = ({
  signature,
  onBack,
  onEdit,
  onAssign,
  onDelete
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const { instance, accounts } = useMsal();

  const getAccessToken = useCallback(async () => {
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['User.Read.All', 'Group.Read.All'],
        account: accounts[0]
      });
      return response.accessToken;
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  }, [instance, accounts]);

  const fetchGroups = useCallback(async () => {
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setLoadingGroups(false);
        return;
      }

      const groupsResponse = await fetch('/api/graph/groups', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (groupsResponse.ok) {
        const groupsData = await groupsResponse.json();
        setGroups(groupsData.groups || []);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoadingGroups(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    if (signature.assignments && signature.assignments.length > 0) {
      fetchGroups();
    } else {
      setLoadingGroups(false);
    }
  }, [signature.assignments, fetchGroups]);

  const handleDelete = () => {
    onDelete(signature.id);
    onBack();
  };

  const assignedGroups = groups.filter(g => signature.assignments?.includes(g.id));
  const displayedGroups = assignedGroups.slice(0, 3);
  const remainingCount = assignedGroups.length - 3;

  return (
    <div className="signature-detail-view">
      <div className="detail-header">
        <button className="btn-back" onClick={onBack}>
          ← Back to Signatures
        </button>
        <div className="detail-actions">
          <button className="btn-back" onClick={onEdit}>
            Edit
          </button>
          <button className="btn-back" onClick={onAssign}>
            Assignments
          </button>
          <button 
            className="btn-back btn-back-danger" 
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete
          </button>
        </div>
      </div>

      <div className="detail-content">
        <h2>{signature.name || 'Untitled Signature'}</h2>
        <div className="signature-info">
          <span className="info-label">Groups Assigned:</span>
          {loadingGroups ? (
            <span className="info-value">Loading...</span>
          ) : signature.assignments?.length === 0 || !signature.assignments ? (
            <span className="info-value">None</span>
          ) : (
            <div className="assigned-groups-display">
              {displayedGroups.map(group => (
                <span key={group.id} className="group-badge">
                  {group.displayName}
                </span>
              ))}
              {remainingCount > 0 && (
                <span className="group-badge remaining-count">
                  +{remainingCount} more
                </span>
              )}
            </div>
          )}
        </div>

        <div className="signature-preview-large">
          <h3>Preview</h3>
          <div 
            className="preview-content" 
            dangerouslySetInnerHTML={{ __html: signature.html }}
          />
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="sync-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sync-error">
              <div className="error-icon">⚠️</div>
              <h3>Delete Signature?</h3>
              <p>Are you sure you want to delete "{signature.name}"? This cannot be undone.</p>
              <div className="modal-actions">
                <button className="btn-back" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </button>
                <button className="btn-back btn-back-danger" onClick={handleDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureDetail;
